import * as THREE from 'three'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import Experience from '../Experience.js'

gsap.registerPlugin(ScrollTrigger)

const COUNT       = 300
const BALL_RADIUS = 0.75
const CAM_Z       = 8
const CAM_FOV     = 35

const smoothstep = (t) => t * t * (3 - 2 * t)

export default class TravelBall {
    constructor(texture) {
        this.experience = new Experience()
        this.time       = this.experience.time
        this.texture    = texture

        this.center        = new THREE.Vector3()
        this.targetCenter  = new THREE.Vector3()
        this.currentColor  = new THREE.Color('#6C63FF')
        this._scratchColor = new THREE.Color()

        this.targetXFrac = 0.74
        this.targetYFrac = 0.5

        this.dissolveScale    = 1
        this.currentOpacity   = 0        // tracks real opacity
        this.hasAppeared      = false    // becomes true after first scroll

        this.WP = []

        this._createOverlay()
        this._initWaypoints()
        this._build()
        this._initScrollTriggers()
    }

    _createOverlay() {
        const vw = document.documentElement.clientWidth
        const vh = document.documentElement.clientHeight

        this._canvas = document.createElement('canvas')
        Object.assign(this._canvas.style, {
            position:      'fixed',
            top:           '0',
            left:          '0',
            width:         '100%',
            height:        '100%',
            pointerEvents: 'none',
            zIndex:        '5',
            mixBlendMode:  'screen',
        })
        document.body.appendChild(this._canvas)

        this._renderer = new THREE.WebGLRenderer({
            canvas:    this._canvas,
            alpha:     true,
            antialias: false,
        })
        this._renderer.setClearColor(0x000000, 0)
        this._renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        this._renderer.setSize(vw, vh)

        this._scene  = new THREE.Scene()
        this._camera = new THREE.PerspectiveCamera(CAM_FOV, vw / vh, 0.1, 1000)
        this._camera.position.set(0, 0, CAM_Z)

        this._calcDimensions(vw, vh)

        this._onResize = () => {
            const w = document.documentElement.clientWidth
            const h = document.documentElement.clientHeight
            this._renderer.setSize(w, h)
            this._camera.aspect = w / h
            this._camera.updateProjectionMatrix()
            this._calcDimensions(w, h)
            this._initWaypoints()
        }
        window.addEventListener('resize', this._onResize)
    }

    _calcDimensions(vw, vh) {
        const vFov = THREE.MathUtils.degToRad(CAM_FOV)
        this._visH = 2 * Math.tan(vFov / 2) * CAM_Z
        this._visW = this._visH * (vw / vh)
    }

    // Convert viewport fractions to Three.js world coords.
    // xFrac=0 → left edge, xFrac=1 → right edge
    // yFrac=0.5 → vertical center (camera never moves)
    _fracToWorld(xFrac, yFrac) {
        return {
            x: (xFrac * 2 - 1) * (this._visW / 2),
            y: (0.5 - yFrac) * this._visH,
        }
    }

    _initWaypoints() {
        // yFrac is always 0.5 — section center aligns with viewport center
        // when the ScrollTrigger fires (that's exactly what center/center means)
        // On mobile (<768px) all waypoints use xFrac=0.50 — narrow viewports
        // make non-center fracs jump to unexpected world positions.
        const isMobile = document.documentElement.clientWidth < 768

        this.WP = [
            { xFrac: isMobile ? 0.50 : 0.74, yFrac: 0.5, hex: '#6C63FF' },
            { xFrac: isMobile ? 0.50 : 0.25, yFrac: 0.5, hex: '#9B96FF' },
            { xFrac: isMobile ? 0.50 : 0.74, yFrac: 0.5, hex: '#00D4FF' },
            { xFrac: 0.50,                   yFrac: 0.5, hex: '#6C63FF' },
            { xFrac: 0.50,                   yFrac: 0.5, hex: '#6C63FF' },
        ]

        const w0 = this._fracToWorld(this.WP[0].xFrac, this.WP[0].yFrac)
        this.center.set(w0.x, w0.y, 0)
        this.targetCenter.copy(this.center)
        this.targetXFrac = this.WP[0].xFrac
        this.targetYFrac = this.WP[0].yFrac
    }

    _build() {
        const positions    = new Float32Array(COUNT * 3)
        const colors       = new Float32Array(COUNT * 3)
        this.restPositions = new Float32Array(COUNT * 3)

        const col = new THREE.Color(this.WP[0].hex)
        const w0  = this._fracToWorld(this.WP[0].xFrac, this.WP[0].yFrac)

        for (let i = 0; i < COUNT; i++) {
            const i3    = i * 3
            const theta = Math.random() * Math.PI * 2
            const phi   = Math.acos(2 * Math.random() - 1)
            const r     = BALL_RADIUS + (Math.random() - 0.5) * 0.18

            const rx = r * Math.sin(phi) * Math.cos(theta)
            const ry = r * Math.cos(phi)
            const rz = r * Math.sin(phi) * Math.sin(theta)

            this.restPositions[i3]     = rx
            this.restPositions[i3 + 1] = ry
            this.restPositions[i3 + 2] = rz

            positions[i3]     = w0.x + rx
            positions[i3 + 1] = w0.y + ry
            positions[i3 + 2] = rz

            colors[i3]     = col.r
            colors[i3 + 1] = col.g
            colors[i3 + 2] = col.b
        }

        const geo = new THREE.BufferGeometry()
        geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
        geo.setAttribute('color',    new THREE.BufferAttribute(colors,    3))

        this.material = new THREE.PointsMaterial({
            size:            0.06,
            map:             this.texture,
            vertexColors:    true,
            transparent:     true,
            opacity:         0,           // starts invisible, fades in on first scroll
            blending:        THREE.AdditiveBlending,
            depthWrite:      false,
            sizeAttenuation: true,
        })

        this.points = new THREE.Points(geo, this.material)
        this._scene.add(this.points)
    }

    _initScrollTriggers() {
        // Step 0→1: hero → features
        ScrollTrigger.create({
            trigger:    document.querySelector('.features'),
            start:      'top 80%',
            end:        'center center',
            scrub:      1,
            onUpdate: (self) => this._onStep(0, 1, self.progress),
        })

        // Step 1→2: features → social-proof
        ScrollTrigger.create({
            trigger:    document.querySelector('.features'),
            start:      'center center',
            endTrigger: document.querySelector('.social-proof'),
            end:        'center center',
            scrub:      1,
            onUpdate: (self) => this._onStep(1, 2, self.progress),
        })

        // Step 2→3: social-proof → pricing
        ScrollTrigger.create({
            trigger:    document.querySelector('.social-proof'),
            start:      'center center',
            endTrigger: document.querySelector('.pricing'),
            end:        'center center',
            scrub:      1,
            onUpdate: (self) => this._onStep(2, 3, self.progress),
        })

        // Step 3→4: pricing → footer-cta
        ScrollTrigger.create({
            trigger:    document.querySelector('.pricing'),
            start:      'center center',
            endTrigger: document.querySelector('.footer-cta'),
            end:        'center center',
            scrub:      1,
            onUpdate: (self) => this._onStep(3, 4, self.progress),
        })
    }

    _onStep(from, to, p) {
        if (!this.WP.length) return
        const s = smoothstep(p)

        // Lerp viewport fractions, convert to world coords each call
        this.targetXFrac = THREE.MathUtils.lerp(this.WP[from].xFrac, this.WP[to].xFrac, s)
        this.targetYFrac = THREE.MathUtils.lerp(this.WP[from].yFrac, this.WP[to].yFrac, s)
        const w = this._fracToWorld(this.targetXFrac, this.targetYFrac)
        this.targetCenter.set(w.x, w.y, 0)

        // Colour
        this.currentColor
            .set(this.WP[from].hex)
            .lerp(this._scratchColor.set(this.WP[to].hex), s)

        // Opacity — fade in only on first step, stay visible for rest
        if (from === 0) {
            this.currentOpacity = THREE.MathUtils.lerp(0, 0.88, Math.min(p * 4, 1))
            if (p > 0.1) this.hasAppeared = true
        } else if (to === 3) {
            // dissolve into pricing halo
            this.currentOpacity = THREE.MathUtils.lerp(0.88, 0.3, s)
            this.dissolveScale  = THREE.MathUtils.lerp(1, 3, s)
        } else if (from === 3) {
            // re-collect from pricing
            const sc = s < 0.8
                ? THREE.MathUtils.lerp(3.0, 0.88, s / 0.8)
                : THREE.MathUtils.lerp(0.88, 1.0, (s - 0.8) / 0.2)
            this.dissolveScale  = sc
            this.currentOpacity = THREE.MathUtils.lerp(0.3, 0.88, s)
        } else {
            // normal travel — always visible
            this.currentOpacity = 0.88
            this.dissolveScale  = 1
        }
    }

    update() {
        if (!this.points || !this.WP.length) return

        const t = this.time.elapsed * 0.001

        // Once appeared, never go below 0.88 unless dissolving
        if (this.hasAppeared && this.currentOpacity < 0.88 && this.dissolveScale === 1) {
            this.currentOpacity = 0.88
        }

        this.material.opacity = this.currentOpacity
        this.center.lerp(this.targetCenter, 0.07)

        const attr = this.points.geometry.attributes.position
        const col  = this.points.geometry.attributes.color
        const rest = this.restPositions
        const cx   = this.center.x
        const cy   = this.center.y
        const sc   = this.dissolveScale
        const cr   = this.currentColor.r
        const cg   = this.currentColor.g
        const cb   = this.currentColor.b

        for (let i = 0; i < COUNT; i++) {
            const i3 = i * 3
            const rx = rest[i3]
            const ry = rest[i3 + 1]
            const rz = rest[i3 + 2]

            const bx = Math.cos(t * 0.38 + ry * 2.1) * 0.022
            const by = Math.sin(t * 0.50 + rx * 1.7 + rz * 0.9) * 0.042

            attr.array[i3]     = cx + (rx + bx) * sc
            attr.array[i3 + 1] = cy + (ry + by) * sc
            attr.array[i3 + 2] = rz * sc

            col.array[i3]     = cr
            col.array[i3 + 1] = cg
            col.array[i3 + 2] = cb
        }

        attr.needsUpdate = true
        col.needsUpdate  = true

        this._renderer.render(this._scene, this._camera)
    }

    destroy() {
        window.removeEventListener('resize', this._onResize)
        this._scene.remove(this.points)
        this.points.geometry.dispose()
        this.material.dispose()
        this._renderer.dispose()
        this._canvas.remove()
        ScrollTrigger.getAll().forEach((t) => t.kill())
    }
}